import {
  Dropdown,
  DropdownRef,
  Input,
  Menu,
  Radio,
  Skeleton,
  Typography,
} from '@recruit-robin/robin-theme/web-components';
import { DotsVertical, Plus } from '@recruit-robin/robin-theme/web-icons';
import { sentenceCase } from 'change-case';
import { createContext, useContext, useMemo, useRef, useState } from 'react';

import { CandidateLabel } from '@/entities/candidate';
import { useTenantDistinctedLabelsQuery } from '@/hooks/tenant/useTenantDistinctedLabelsQuery';
import { useDictionary } from '@/shared/lib/i18n';
import { CloseButton } from '@/shared/ui/close-button';
import { useStyles } from './OutcomePill.styles';

type GroupContextType = {
  selectedValue: string[];
  onSelect: (value: string) => void;
};

const GroupContext = createContext<GroupContextType>({
  selectedValue: [],
  onSelect: () => {},
});

const Group = ({
  children,
  selectedValue,
  onSelect,
}: {
  children: React.ReactNode;
  selectedValue: string[];
  onSelect: (value: string) => void;
}) => {
  const classes = useStyles();

  return (
    <div css={classes.group}>
      <GroupContext.Provider value={{ selectedValue, onSelect }}>{children}</GroupContext.Provider>
    </div>
  );
};

type DropdownOption = {
  label: string;
  value: string;
};

const OutcomePillDropdown = ({ initialOptions }: { initialOptions: string[] }) => {
  const dictionary = useDictionary();
  const classes = useStyles();
  const dropdownRef = useRef<DropdownRef>(null);
  const { selectedValue, onSelect } = useContext(GroupContext);
  const [search, setSearch] = useState('');
  const { data: distinctedLabels, isFetching: isFetchingDistinctedLabels } = useTenantDistinctedLabelsQuery();

  const handleSelectOption = (option: string) => {
    onSelect(option);
  };

  const options = useMemo(() => {
    const staticOptions: string[] = Object.values(CandidateLabel);
    const currentCustomOptions = Array.from(new Set([...initialOptions, ...selectedValue])).filter(
      (option) => !staticOptions.includes(option),
    );

    const neutralOptions: DropdownOption[] = [];
    const negativeOptions: DropdownOption[] = [
      { label: dictionary.notOpenToWork, value: CandidateLabel.NotOpenToWork },
    ];
    const customOptions: DropdownOption[] = currentCustomOptions
      .filter((option) => !distinctedLabels?.includes(option))
      .map((option) => ({ label: option, value: option }));

    distinctedLabels?.forEach((label) => {
      customOptions.push({ label: sentenceCase(label), value: label });
    });

    return {
      ...(neutralOptions.length > 0 ? { neutralOptions } : {}),
      ...(negativeOptions.length > 0 ? { negativeOptions } : {}),
      ...(customOptions.length > 0 ? { customOptions } : {}),
    };
  }, [initialOptions, selectedValue, distinctedLabels]);

  const filteredOptions = useMemo(() => {
    const toSearch = (option: DropdownOption) => option.label.toLowerCase().includes(search.toLowerCase());

    const neutralOptions = options.neutralOptions?.filter(toSearch) || [];
    const negativeOptions = options.negativeOptions?.filter(toSearch) || [];
    const customOptions = options.customOptions?.filter(toSearch) || [];

    return {
      ...(neutralOptions.length > 0 ? { neutralOptions } : {}),
      ...(negativeOptions.length > 0 ? { negativeOptions } : {}),
      ...(customOptions.length > 0 ? { customOptions } : {}),
    };
  }, [options, search]);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const firstAvailableOption =
        filteredOptions.neutralOptions?.[0] ||
        filteredOptions.negativeOptions?.[0] ||
        filteredOptions.customOptions?.[0];

      if (firstAvailableOption) {
        return handleSelectOption(firstAvailableOption.value);
      }

      if (search.length > 0) {
        return handleSelectOption(search);
      }
    }
  };

  return (
    <Dropdown
      ref={dropdownRef}
      autoUpdateMenuPosition
      flipMenu={false}
      shouldUsePortal={false}
      renderMenu={
        <Menu.Root customCss={classes.dropdownMenu}>
          <div css={classes.dropdownMenuInputContainer}>
            <Input
              placeholder={dictionary.searchOrCreateLabel}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              endIcon={search.length > 0 ? <CloseButton size={12} onClose={() => setSearch('')} /> : undefined}
              onKeyUp={handleKeyUp}
              contained
            />
          </div>

          {filteredOptions.neutralOptions && (
            <>
              <Typography
                customCss={classes.dropdownMenuHeader}
                variant={(v) => v.supporting['label-tiny']}
                color={(c) => c.neutral.variant[60]}>
                {dictionary.neutralOutcomes}
              </Typography>
              {filteredOptions.neutralOptions.map((option) => (
                <Menu.Item.Root
                  key={option.value}
                  selected={selectedValue.includes(option.value)}
                  onClick={() => handleSelectOption(option.value)}>
                  <Menu.Item.Radio radioVariant="minimalist" />
                  <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[60]}>
                    {option.label}
                  </Typography>
                </Menu.Item.Root>
              ))}
            </>
          )}

          {filteredOptions.negativeOptions && (
            <>
              <Typography
                customCss={classes.dropdownMenuHeader}
                variant={(v) => v.supporting['label-tiny']}
                color={(c) => c.neutral.variant[60]}>
                {dictionary.negativeOutcomes}
              </Typography>
              {filteredOptions.negativeOptions.map((option) => (
                <Menu.Item.Root
                  key={option.value}
                  selected={selectedValue.includes(option.value)}
                  onClick={() => handleSelectOption(option.value)}>
                  <Menu.Item.Radio radioVariant="minimalist" />
                  <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[60]}>
                    {option.label}
                  </Typography>
                </Menu.Item.Root>
              ))}
            </>
          )}

          {(filteredOptions.customOptions || search.length > 0 || isFetchingDistinctedLabels) && (
            <Typography
              customCss={classes.dropdownMenuHeader}
              variant={(v) => v.supporting['label-tiny']}
              color={(c) => c.neutral.variant[60]}>
              {dictionary.customLabels}
            </Typography>
          )}
          {filteredOptions.customOptions ? (
            filteredOptions.customOptions.map((option) => (
              <Menu.Item.Root
                key={option.value}
                onClick={() => handleSelectOption(option.value)}
                selected={selectedValue.includes(option.value)}>
                <Menu.Item.Radio radioVariant="minimalist" />
                <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[60]} inline ellipsis>
                  {option.label}
                </Typography>
              </Menu.Item.Root>
            ))
          ) : search.length > 0 ? (
            <Menu.Item.Root onClick={() => handleSelectOption(search)}>
              <Plus size={16} />
              <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[60]}>
                {`${dictionary.add} "${search}"`}
              </Typography>
            </Menu.Item.Root>
          ) : null}

          {isFetchingDistinctedLabels &&
            Array.from({ length: 4 }).map((_, index) => (
              <Menu.Item.Root key={index}>
                <Skeleton variant="square" width={Math.random() * 110 + 90} height={13} />
              </Menu.Item.Root>
            ))}
        </Menu.Root>
      }>
      {({ isOpen }) => (
        <div css={[classes.pill, isOpen && classes.pillOpen]}>
          <DotsVertical size={16} color={(c) => c.neutral.variant[60]} />
          <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[60]}>
            {dictionary.more}
          </Typography>
        </div>
      )}
    </Dropdown>
  );
};

export const OutcomePill = ({ label, value }: { label: string; value: string }) => {
  const classes = useStyles();
  const { selectedValue, onSelect } = useContext(GroupContext);

  return (
    <div css={classes.pill} onClick={() => onSelect(value)}>
      <Radio.Item customCss={classes.radio} checked={selectedValue.includes(value)} variant="minimalist" />
      <Typography
        variant={(v) => v.supporting.label}
        color={(c) => c.neutral.variant[selectedValue.includes(value) ? 80 : 60]}>
        {label}
      </Typography>
    </div>
  );
};

OutcomePill.Group = Group;
OutcomePill.Dropdown = OutcomePillDropdown;
